import React from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {Size} from 'types/Theme'
import {Spacing} from 'types/Theme'

import {Button} from '@vio/atlas/Button'
import OpacityAnimation from '@vio/atlas/helpers/OpacityAnimation'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'

export type SwiperButtonSizeType = Exclude<Size, 'xs' | 'xxl' | 'lg' | 'xl'>

const MAP_TOGGLE_BUTTON_WIDTH = 146

const BaseArrowGradient = styled.div<{isVisible: boolean; shadowWidth: number}>`
  visibility: ${({isVisible}) => (isVisible ? 'visible' : 'hidden')};
  opacity: ${({isVisible}) => (isVisible ? 1 : 0)};
  transition: all 0.3s ease-in-out;
  position: absolute;
  top: 0;
  height: 100%;
  width: ${({shadowWidth = 120}) => shadowWidth}px;
  display: flex;
  background: ${cssTheme.colors.background.neutral.primary};
  pointer-events: none;
  z-index: 1;
  transform: translate3d(0, 0, 0);
`

const LeftArrowGradient = styled(BaseArrowGradient)<{
  left?: number
  marginLeft: Spacing
  paddingLeft: Spacing
}>`
  background: linear-gradient(
    90deg,
    ${cssTheme.colors.background.neutral.primary} 10%,
    transparent 100%
  );

  [dir='rtl'] & {
    background: linear-gradient(
      270deg,
      ${cssTheme.colors.background.neutral.primary} 10%,
      transparent 100%
    );
  }

  inset-inline-start: ${({left = 0}) => left}px;
  justify-content: flex-start;
  margin-inline-start: ${({marginLeft}) => cssTheme.layout.spacing[marginLeft]};
  padding-inline-start: ${({paddingLeft}) =>
    cssTheme.layout.spacing[paddingLeft]};
`

const RightArrowGradient = styled(BaseArrowGradient)<{
  right?: number
  marginRight: Spacing
  paddingRight: Spacing
}>`
  background: linear-gradient(
    -90deg,
    ${cssTheme.colors.background.neutral.primary} 31%,
    transparent 93%
  );
  [dir='rtl'] & {
    background: linear-gradient(
      90deg,
      ${cssTheme.colors.background.neutral.primary} 31%,
      transparent 100%
    );
  }
  inset-inline-end: ${({right = MAP_TOGGLE_BUTTON_WIDTH}) => right}px;
  justify-content: flex-end;
  margin-inline-end: ${({marginRight}) => cssTheme.layout.spacing[marginRight]};
  padding-inline-end: ${({paddingRight = 's000'}) =>
    cssTheme.layout.spacing[paddingRight]};
`

const StyledButton = styled(Button)`
  align-self: center;
  pointer-events: all;
`

interface Props {
  /** Whether or not the button should show */
  isVisible: boolean
  /** The direction of the arrow */
  direction: 'left' | 'right'
  /** Callback fired on button click */
  onClick: () => void
  /** Distance from right side of wrapper */
  right?: number
  /** Distance from left side of wrapper */
  left?: number
  /** margin right */
  marginRight?: Spacing
  /** margin left */
  marginLeft?: Spacing
  /** padding right */
  paddingRight?: Spacing
  /** padding left */
  paddingLeft?: Spacing
  /** shadow width */
  shadowWidth?: number
  /** DATA ID */
  dataId?: string
  /** RTL  */
  isRtl?: boolean
  /** Button size */
  size?: SwiperButtonSizeType
}

/**
 * Button to handle the horizontal scrolling on the filters bar
 */
export const ScrollButton: React.FC<Props> = ({
  isVisible,
  direction,
  onClick,
  left = 0,
  right = MAP_TOGGLE_BUTTON_WIDTH,
  marginRight = 's600',
  marginLeft = 's000',
  paddingRight = 's000',
  paddingLeft = 's000',
  shadowWidth = 120,
  dataId,
  isRtl,
  size = 'sm'
}) => {
  const {formatMessage} = useIntl()

  if (direction === 'left')
    return (
      <div data-chromatic="ignore" data-id={dataId}>
        <OpacityAnimation>
          <LeftArrowGradient
            isVisible={isVisible}
            left={left}
            shadowWidth={shadowWidth}
            marginLeft={marginLeft}
            paddingLeft={paddingLeft}
          >
            <StyledButton
              variant="tertiary"
              size={size}
              isRounded
              isFloating
              onClick={onClick}
              ariaLabel={formatMessage({
                id: 'filters.scrollLeft',
                defaultMessage: 'Scroll left'
              })}
              iconStart={
                <Icon
                  name={isRtl ? 'ChevronRight' : 'ChevronLeft'}
                  size={size}
                />
              }
            />
          </LeftArrowGradient>
        </OpacityAnimation>
      </div>
    )

  return (
    <div data-chromatic="ignore" data-id={dataId}>
      <OpacityAnimation>
        <RightArrowGradient
          isVisible={isVisible}
          right={right}
          marginRight={marginRight}
          paddingRight={paddingRight}
          shadowWidth={shadowWidth}
        >
          <StyledButton
            variant="tertiary"
            size={size}
            isRounded
            isFloating
            onClick={onClick}
            ariaLabel={formatMessage({
              id: 'filters.scrollRight',
              defaultMessage: 'Scroll right'
            })}
            iconStart={
              <Icon name={isRtl ? 'ChevronLeft' : 'ChevronRight'} size={size} />
            }
          />
        </RightArrowGradient>
      </OpacityAnimation>
    </div>
  )
}
